<section class="grid gap-4">
  <div class="flex items-center justify-between gap-3">
    <page-title title="Intervention" /><a mat-stroked-button routerLink="/interventions"
      >Back to interventions</a
    >
  </div>
  @if (store.selected(); as intervention) {
    <!-- A declined consent only flags the case: it is not closed -->
    @if (intervention.consentDeclined) {
      <div class="flex items-center gap-2 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="status">
        <mat-icon class="!text-base">flag</mat-icon>
        <span>Consent declined. The member declined a consent on this case. The case stays open.</span>
      </div>
    }

    <!-- The stage the case is in and the finer status inside it -->
    <div class="flex flex-wrap items-center gap-2 text-sm text-slate-700">
      <span class="font-semibold text-slate-900">{{ intervention.interventionNumber }}</span>
      <span class="rounded-full bg-slate-100 px-3 py-0.5 font-medium">Stage: {{ intervention.status }}</span>
      @if (intervention.subStatus) {
        <span class="rounded-full bg-amber-50 px-3 py-0.5 font-medium text-amber-900">{{ intervention.subStatus }}</span>
      }
    </div>

    @if (ready()) {
      <!-- The wizard: the steps of the case with how far each has got, and the work that comes after them. The case opens at the step that is next. -->
      <wizard-steps-nav
        [steps]="steps()"
        [sections]="sections()"
        [active]="view()"
        [showProgress]="progressKnown()"
        [nextStepKey]="store.progress()?.nextStepKey ?? null"
        [lockedSteps]="lockedSteps()"
        [taskSection]="taskView()"
        [taskName]="nextTask()?.name ?? null"
        (selected)="open($event)" />
    }

    <!-- The step that is open on the left; the context of the case and its audit trail beside it -->
    @if (ready()) {
      <div class="grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div class="min-w-0">
          @if (ciaPart(); as part) {
            <!-- The parts of the clinical assessment are steps 3 to 6; one form stays on screen while the case moves between them -->
            @if (view() === 'FunctionalImpactAndRisk' && isRiskScreenerEligible(intervention)) {
              <risk-screener-panel class="mb-4 block" [interventionId]="id" [interventionStatus]="intervention.status" />
            }
            <cia-host
              [interventionId]="id"
              [hasCia]="intervention.hasCia"
              [part]="part"
              [interventionStatus]="intervention.status"
              [subStatus]="intervention.subStatus"
              (changed)="onCiaChanged()">
              <!-- The goals are entered in Treatment planning, between the plan and the lock -->
              @if (part === 'treatment' && intervention.usesTreatmentGoals) {
                <treatment-goals-panel [interventionId]="id" [interventionStatus]="intervention.status" />
              }
            </cia-host>
          } @else {
            @switch (view()) {
              @case ('NewRequest') {
                <new-request-form
                  [busy]="store.saving()"
                  [readOnly]="!canUpdate() || intervention.status === 'Closed'"
                  [initialValue]="intervention"
                  [interventionTypes]="store.interventionTypes()"
                  (updated)="saveIntervention($event)" />
                <intervention-referrer-panel class="mt-4 block" [interventionId]="id" [interventionStatus]="intervention.status" />
              }
              @case ('Eligibility') {
                <intervention-eligibility-panel [interventionId]="id" [interventionStatus]="intervention.status" />
              }
              @case ('PresentingProblem') {
                <!-- A type without an assessment records the coded problem here (and the risk screener, where it has one) -->
                <presenting-problem-panel [interventionId]="id" [interventionStatus]="intervention.status" />
                @if (isRiskScreenerEligible(intervention)) {
                  <risk-screener-panel class="mt-4 block" [interventionId]="id" [interventionStatus]="intervention.status" />
                }
              }
              @case ('TreatmentPlanning') {
                <treatment-goals-panel [interventionId]="id" [interventionStatus]="intervention.status" />
              }
              @case ('ConsentAndPreferences') {
                <intervention-consents-panel [interventionId]="id" [interventionStatus]="intervention.status" />
                @if (hasReferralModel(intervention)) {
                  <referral-preference-panel class="mt-4 block" [interventionId]="id" [interventionStatus]="intervention.status" />
                }
              }
              @case ('ReferralMatching') {
                <!-- A provider is told the referral (the member, the reason, the problems and the goals) above the referral it answers -->
                @if (isProvider()) {
                  <provider-referral-summary class="mb-4 block" [interventionId]="id" [interventionStatus]="intervention.status" />
                }
                @if (hasReferralModel(intervention)) {
                  <provider-referral-panel [interventionId]="id" [interventionStatus]="intervention.status" />
                } @else {
                  <intervention-affiliate-assignment [interventionId]="id" [canAssign]="canAssignAffiliate()" (assigned)="onAffiliateAssigned()" />
                }
              }
              @case ('tasks') {
                <intervention-task-panel [interventionId]="id" />
              }
              @case ('sessions') {
                <intervention-sessions-panel
                  [interventionId]="id"
                  [interventionStatus]="intervention.status"
                  [usesSessionNotes]="usesSessionNotes(intervention)" />
              }
              @case ('reports') {
                <intervention-reports-panel [interventionId]="id" [interventionStatus]="intervention.status" />
              }
              @case ('history') {
                <history-list title="Intervention history" emptyLabel="No history found for this intervention." [history]="store.history()" />
              }
            }
          }

          <!-- Previous and next step (the sections after the wizard have none) -->
          @if (stepNumber(); as number) {
            <div class="mt-4 flex items-center justify-between gap-3">
              @if (previousStep(); as previous) {
                <button mat-stroked-button type="button" (click)="open(previous.key)">
                  <mat-icon>arrow_back</mat-icon>
                  Previous: {{ previous.label }}
                </button>
              } @else {
                <span></span>
              }
              <span class="text-sm text-slate-500">Step {{ number }} of {{ steps().length }}</span>
              @if (nextStep(); as next) {
                <button
                  mat-flat-button
                  type="button"
                  [disabled]="lockedSteps().includes(next.key)"
                  [matTooltip]="lockedSteps().includes(next.key) ? 'Record that the member is eligible first' : ''"
                  (click)="open(next.key)">
                  Next: {{ next.label }}
                  <mat-icon iconPositionEnd>arrow_forward</mat-icon>
                </button>
              } @else {
                <span></span>
              }
            </div>
          }

          <!-- Moving the case on by hand, or closing it: not part of any step -->
          <intervention-stage-panel
            class="mt-4 block"
            [intervention]="intervention"
            [lifecycleStages]="store.lifecycleStages()"
            [interventionTypes]="store.interventionTypes()"
            [suggestedClosureOutcomeId]="suggestedClosureOutcomeId()"
            [busy]="store.saving()"
            [readOnly]="!canUpdate()"
            (submitted)="saveIntervention($event)" />
        </div>

        <aside class="min-w-0" aria-label="Case context">
          <wizard-side-panels [context]="store.progress()?.context ?? null" [history]="store.history()" (openHistory)="open('history')" />
        </aside>
      </div>
    } @else {
      <mat-card class="p-6">Opening the case...</mat-card>
    }
  } @else {
    <mat-card class="p-6">Loading intervention...</mat-card>
  }
</section>
